import { gql } from '@apollo/client';
import { Box, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack } from '@chakra-ui/react';
import { t } from 'i18next';
import { TableInstance } from 'react-table';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { defaultPageSizes } from '~components/ui/PageSizeSelector';
import { SubscriptionCustomersTableV2CustomerFragment as CustomerFragment } from './__generated__/SubscriptionCustomersTable.graphql';
import { SubscriptionCustomersTableV2Filter } from './Filtering';
import {
  SubscriptionCustomersTableV2PageSizeSelector,
  SubscriptionCustomersTableV2Pagination,
} from './Pagination';

interface SubscriptionCustomersTableV2Props {
  table: TableInstance<CustomerFragment>;
  currentCustomerIds: string[];
  isActive: boolean;
}

export function SubscriptionCustomersTableV2({
  table,
  currentCustomerIds,
  isActive,
}: SubscriptionCustomersTableV2Props) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = table;

  return (
    <VStack spacing="6" alignItems="stretch">
      <Text color="gray.500">{t('subscriptionCustomerLink')}</Text>{' '}
      <SubscriptionCustomersTableV2Filter table={table} />
      <Table {...getTableProps()} variant="simple" width="100%" marginTop="8">
        <Thead>
          {headerGroups.map((headerGroup) => {
            const headerGroupProps = headerGroup.getHeaderGroupProps();

            return (
              <Tr {...headerGroupProps} key={headerGroupProps.key}>
                {headerGroup.headers.map((column) => {
                  const sortProps = column.getSortByToggleProps();
                  const sortColumn = sortProps.onClick;
                  const headerProps = column.getHeaderProps(sortProps);

                  return (
                    <Th
                      {...headerProps}
                      key={headerProps.key}
                      color={column.isSorted ? 'gray.700' : 'gray.600'}
                      minWidth={`${column.minWidth}px`}
                      maxWidth={`${column.maxWidth}px`}
                      width={column.width}
                      _hover={
                        column.disableSortBy
                          ? {}
                          : {
                              textColor: 'gray.800',
                            }
                      }
                      onClick={sortColumn}
                    >
                      <Box display="flex" flexDirection="row" alignItems="center">
                        <Box flex="1">{column.render('Header')}</Box>
                        {column.canSort && (
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <ChevronDownIcon display="inherit" />
                              ) : (
                                <ChevronUpIcon display="inherit" />
                              )
                            ) : (
                              <ChevronDownIcon visibility="hidden" />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Th>
                  );
                })}
              </Tr>
            );
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            const notcurrentCustomer = !currentCustomerIds.includes(row.original.id);

            const isDisabled = !isActive && notcurrentCustomer;

            return (
              <Tooltip
                label={isDisabled ? t('subscriptionCustomerLinkTooltip2') : ''}
                isDisabled={!isDisabled}
                hasArrow
                key={rowProps.key}
              >
                <Tr
                  {...rowProps}
                  {...(isDisabled ? { background: 'gray.50' } : { background: 'blue.' })}
                >
                  {row.cells.map((cell) => {
                    const cellProps = cell.getCellProps();
                    return (
                      <Td
                        {...cellProps}
                        {...(isDisabled ? { opacity: 0.5 } : {})}
                        minWidth={`${cell.column.minWidth}px`}
                        maxWidth={`${cell.column.maxWidth}px`}
                        width={cell.column.width}
                        key={cellProps.key}
                      >
                        {cell.render('Cell')}
                      </Td>
                    );
                  })}
                </Tr>
              </Tooltip>
            );
          })}
        </Tbody>
      </Table>
      <Box display="flex">
        <Box flex="1">
          <SubscriptionCustomersTableV2PageSizeSelector
            table={table}
            sizes={[15, ...defaultPageSizes]}
          />
        </Box>
        <SubscriptionCustomersTableV2Pagination table={table} />
      </Box>
    </VStack>
  );
}

SubscriptionCustomersTableV2.graphql = {
  fragments: {
    SubscriptionCustomersTableV2Customer: gql`
      fragment SubscriptionCustomersTableV2Customer on Customer {
        id
        name
        displayCount
        waveSubscriptionUsage
        waveSubscriptions {
          id
          validUntil
          status
        }
        waveSubscriptionUsage
      }
    `,
  },
};
